import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Typography,
  Grid,
  Box,
  Avatar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import Person from '@mui/icons-material/Person';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import { useGetJobQuery, useUpdateJobAssignmentMutation } from '@diditusa/shared/api/jobsApi';
import { useAuth } from '../../context/AuthContext';
import {
  useEmailJobDetailsToAgentMutation,
  useLazyGetCompanyEmployeesQuery,
} from '@diditusa/shared/api/companyApi';
import { getErrorMessage } from '@diditusa/shared/api/apiUtils';
import { format } from 'date-fns';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
const CompanyJobDetails = () => {
  const { user } = useAuth();
  const companyId = user?.company?.id;
  const { jobId } = useParams();
  const { data: job, isLoading, error } = useGetJobQuery(parseInt(jobId!));

  const [getCompanyEmployees, { data: employees, ...getEmployeesState }] =
    useLazyGetCompanyEmployeesQuery();

  const [updateJobAssignments, updateJobAssignmentsState] = useUpdateJobAssignmentMutation();
  const [sendJobDetailsEmail, sendJobDetailsEmailState] = useEmailJobDetailsToAgentMutation();
  const [showEmployeeSelect, setShowEmployeeSelect] = useState(false);
  const [selectedEmployees, _setSelectedEmployees] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (companyId) {
      getCompanyEmployees(companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (job) {
      _setSelectedEmployees(job.jobAssignments?.map((ja) => ja.userId) || []);
    }
  }, [job]);

  const setSelectedEmployees = (newEmployees: string[]) => {
    setDirty(true);
    _setSelectedEmployees(newEmployees);
  };

  useEffect(() => {
    if (updateJobAssignmentsState.isSuccess) {
      setDirty(false);
      setShowEmployeeSelect(false);
    }
  }, [updateJobAssignmentsState.isSuccess]);

  const saveChanges = async () => {
    updateJobAssignments({ jobId: parseInt(jobId!), req: { employeeIds: selectedEmployees } });
  };

  const onEmailDetailsClick = () => {
    sendJobDetailsEmail({ companyId: companyId!, jobId: parseInt(jobId!) });
  };

  const getEmployeeMenuItems = () => {
    if (getEmployeesState.isLoading) {
      return <MenuItem disabled={true}>Loading...</MenuItem>;
    }

    if (getEmployeesState.isError) {
      return <MenuItem disabled={true}>Error loading employees</MenuItem>;
    }

    if (!employees || employees.length === 0) {
      return <MenuItem disabled={true}>No employees found</MenuItem>;
    }

    return employees.map((employee) => (
      <MenuItem key={employee.id} value={employee.id}>
        {`${employee.firstName} ${employee.lastName}`}
      </MenuItem>
    ));
  };

  if (isLoading) {
    return <p>Loading job details...</p>;
  }

  if (error) {
    return <p>Error: {getErrorMessage(error)}</p>;
  }

  if (!job) {
    return <p>Job not found.</p>;
  }

  return (
    <Box component='main' sx={{ flexGrow: 1, textAlign: 'left' }}>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h4' fontWeight='bold'>
            Job Details: {job.reservationCode}
          </Typography>
        </Box>
        <Box display='flex' alignItems='center' gap={2} mb={4}>
          {job.createdBy?.imageUrl ? (
            <Avatar src={job.createdBy.imageUrl} sx={{ width: 96, height: 96 }} />
          ) : (
            <Person sx={{ width: 96, height: 96 }} />
          )}
          <Box>
            <Typography variant='h6' fontWeight={700}>
              {job.createdBy
                ? `${job.createdBy.firstName} ${job.createdBy.lastName}`
                : job.createdById}
            </Typography>
            <Typography color='text.secondary'>
              {job.createdBy ? job.createdBy.email : ''}
            </Typography>
          </Box>
        </Box>

        <Section title='Description'>
          <Typography>{job.description}</Typography>
        </Section>

        <Section title='Job Details'>
          <DetailRow
            label='Job type'
            value={job.securityOption ? job.securityOption.title : `${job.securityOptionId}`}
          />
          <DetailRow label='Location' value={job.location} />
          <DetailRow label='Date' value={job.startTime.toFormat('EEEE, MM/dd/yyyy')} />
          <DetailRow label='Start time' value={job.startTime.toFormat('hh:mm a ZZZZ')} />
          <DetailRow label='Status' value={job.status.toString()} />
        </Section>

        <Grid container sx={{ display: 'flex' }}>
          <Grid size={6} sx={{ xs: 12 }}>
            <Section title='Assigned Agents'>
              {job.jobAssignments && job.jobAssignments.length > 0 ? (
                job.jobAssignments.map((ja) => (
                  <Typography>
                    {ja.user ? `${ja.user.firstName} ${ja.user.lastName}` : ja.userId}
                  </Typography>
                ))
              ) : (
                <Typography>Unassigned</Typography>
              )}
              {showEmployeeSelect && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id='guard-select-label'>Select Guards</InputLabel>
                  <Select
                    sx={{ mr: 4 }}
                    labelId='guard-select-label'
                    multiple
                    value={selectedEmployees}
                    onChange={(e) => setSelectedEmployees(e.target.value as string[])}
                    input={<OutlinedInput label='Select Guards' />}
                    renderValue={(selected) => {
                      if (!employees) {
                        return;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => (
                            <Chip
                              key={id}
                              label={employees.find((g) => g.id === id)?.firstName || id}
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {getEmployeeMenuItems()}
                  </Select>
                </FormControl>
              )}
              {updateJobAssignmentsState.isError && (
                <Typography>{`Error updating assigned agents ${
                  (updateJobAssignmentsState.error as any).data.message
                }`}</Typography>
              )}
              <LoadingIndicator isVisible={updateJobAssignmentsState.isLoading} />

              <Button variant='outlined' onClick={() => setShowEmployeeSelect(!showEmployeeSelect)}>
                {showEmployeeSelect ? 'Cancel' : job.jobAssignments ? 'Change' : 'Assign to Agent'}
              </Button>
            </Section>
            {job.jobAssignments && job.jobAssignments.length > 0 && !showEmployeeSelect && (
              <Box>
                <Button onClick={onEmailDetailsClick} variant='contained'>
                  Email details to Agent
                </Button>
                <LoadingIndicator isVisible={sendJobDetailsEmailState.isLoading} />
                {sendJobDetailsEmailState.isError && <ErrorOutlineIcon color='error' />}
                {sendJobDetailsEmailState.isSuccess && <CheckCircleOutlineIcon color='success' />}
              </Box>
            )}
          </Grid>
        </Grid>
        {dirty && (
          <Box>
            <Button variant='contained' color='primary' onClick={saveChanges}>
              Save changes
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box mb={4}>
    <Typography variant='h6' fontWeight={700} gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Grid container spacing={2} sx={{ mb: 1 }}>
    <Grid size={6} sx={{ xs: 6 }}>
      <Typography color='text.secondary' fontWeight={'bold'}>
        {label}
      </Typography>
    </Grid>
    <Grid sx={{ xs: 6 }} size={6}>
      <Typography>{value}</Typography>
    </Grid>
  </Grid>
);

export default CompanyJobDetails;
